import type { ReactNode } from "react";
import type { HandState, PlayerIndex } from "@joker-holdem/core";
import { formatBb, positionLabel } from "../engine/selectors.js";

interface PlayerPanelProps {
  hand: HandState;
  seat: PlayerIndex;
  name: string;
  /**
   * Center-row content, e.g. the opponent's hole cards. Both panels share
   * the same left/center/right structure (name+badge / cards / stack) so
   * the stack numbers land on the same right-hand edge for both players
   * (フィードバック #1, #4) -- the hero panel simply passes nothing here.
   */
  children?: ReactNode;
  /**
   * Shrinks this panel's padding/margin (2026-07-17 フィードバック #3) --
   * only ever passed for the hero's panel at showdown, since it sits below
   * the board and can shrink without moving it (item 4), to help the
   * board's now-taller joker-interpretation cards fit without scrolling.
   */
  compact?: boolean;
}

export function PlayerPanel({ hand, seat, name, children, compact }: PlayerPanelProps) {
  const player = hand.players[seat];
  const isTurn = hand.toAct === seat && hand.result === null;
  const isDealer = hand.button === seat;
  const position = positionLabel(hand, seat);

  return (
    <div
      className={`player-panel ${isTurn ? "player-panel-active" : ""} ${player.folded ? "player-panel-folded" : ""} ${compact ? "player-panel-compact" : ""}`}
    >
      <div className="player-panel-left">
        <span className="player-name">{name}</span>
        <span className="position-badge">{position}</span>
        {isDealer && <span className="dealer-button">D</span>}
      </div>
      <div className="player-panel-center">{children}</div>
      <div className="player-panel-right">
        <span className="player-stack">{formatBb(player.stack)}</span>
      </div>
    </div>
  );
}
